import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Calculator, CheckCircle, Star, Lock, Play, Trophy, Loader2, Sparkles
} from 'lucide-react';

// Map child age band → highest Kiddom Math grade appropriate for them.
const maxGradeForChild = (child) => {
  if (!child) return 'K';
  // Explicit parent override wins.
  const explicit = (child.grade_level || '').toString().trim().toUpperCase();
  if (explicit) return explicit.replace(/[^0-9K]/g, '') || 'K';
  // Compute from birth_date, fall back to age_group.
  if (child.birth_date) {
    try {
      const bd = new Date(child.birth_date);
      const now = new Date();
      const months = (now.getFullYear() - bd.getFullYear()) * 12 + (now.getMonth() - bd.getMonth());
      if (months < 60)   return 'K';   // 3-4  → still show K as intro
      if (months < 72)   return 'K';   // 5    → Kindergarten
      if (months < 84)   return '1';   // 6
      if (months < 96)   return '2';   // 7
      if (months < 108)  return '3';   // 8
      if (months < 120)  return '4';   // 9
      if (months < 132)  return '5';   // 10
      if (months < 144)  return '6';   // 11
      if (months < 156)  return '7';   // 12
      return '8';
    } catch { /* noop */ }
  }
  const ag = (child.age_group || '').toString().toLowerCase();
  if (ag.includes('pre') || ag.includes('toddler') || ag.includes('infant')) return 'K';
  if (ag.includes('early')) return '1';
  if (ag.includes('elementary')) return '4';
  if (ag.includes('middle')) return '8';
  return 'K';
};

// Grade → level mapping mirrors seed_music_kiddom.py
const GRADE_ORDER = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];

const GRADE_META = {
  'K':  { label: 'Kindergarten',  color: '#F59E0B', hint: 'Counting, shapes & first numbers' },
  '1':  { label: 'Grade 1',       color: '#F97316', hint: 'Addition, subtraction & place value' },
  '2':  { label: 'Grade 2',       color: '#EF4444', hint: 'Time, money & word problems' },
  '3':  { label: 'Grade 3',       color: '#8B5CF6', hint: 'Multiplication, division & fractions' },
  '4':  { label: 'Grade 4',       color: '#6366F1', hint: 'Multi-digit ops & decimals' },
  '5':  { label: 'Grade 5',       color: '#0EA5E9', hint: 'Volume, PEMDAS & coordinates' },
  '6':  { label: 'Grade 6',       color: '#10B981', hint: 'Ratios & integers' },
  '7':  { label: 'Grade 7',       color: '#14B8A6', hint: 'Percentages & equations' },
  '8':  { label: 'Grade 8',       color: '#3B82F6', hint: 'Pythagorean theorem & functions' },
};

const KiddomMathPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [child, setChild] = useState(null);
  const [completedIds, setCompletedIds] = useState(new Set());

  useEffect(() => { load(); }, [childId]);

  const load = async () => {
    try {
      const [childRes, actsRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/activities?subject=math`)
      ]);
      setChild(childRes.data);
      const acts = Array.isArray(actsRes.data) ? actsRes.data : (actsRes.data.activities || []);
      const kiddom = acts.filter(a => (a.id || '').startsWith('kmath-'));
      setActivities(kiddom);

      try {
        const progressRes = await api.get(`/children/${childId}/progress`);
        const done = new Set(
          (progressRes.data.completed_activities || progressRes.data.progress || [])
            .map(p => p.activity_id || p.id)
        );
        setCompletedIds(done);
      } catch { /* progress optional */ }
    } catch (e) {
      toast.error('Failed to load Kiddom curriculum');
    } finally {
      setLoading(false);
    }
  };

  const groupedByGrade = GRADE_ORDER.map(g => ({
    grade: g,
    meta: GRADE_META[g],
    lessons: activities
      .filter(a => (a.content?.grade || '') === g)
      .sort((a, b) => (a.id || '').localeCompare(b.id || ''))
  })).filter(g => g.lessons.length > 0);

  // Age-band gating: any grade above the child's max is shown as "Coming up next!"
  const childMax = maxGradeForChild(child);
  const gradeIndex = (g) => GRADE_ORDER.indexOf(g);
  const isAboveBand = (g) => gradeIndex(g) > gradeIndex(childMax);

  const total = activities.length;
  const done = activities.filter(a => completedIds.has(a.id)).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-500 to-orange-500 text-white sticky top-0 z-40 shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={`/learn/${childId}`} data-testid="back-to-hub">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="text-center">
            <h1 className="font-fredoka text-xl font-bold">Kiddom Math</h1>
            <p className="text-xs text-white/80">
              CCSS-aligned K-8 · <span data-testid="kmath-grade-chip">Grade: {childMax}</span>
            </p>
          </div>
          <Badge className="bg-white/20 text-white border-0">
            <Trophy className="w-3 h-3 mr-1" /> {done}/{total}
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Progress Card */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-0 shadow-md mb-8 bg-gradient-to-br from-red-50 to-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                  <Calculator className="w-7 h-7 text-red-500" />
                </div>
                <div className="flex-1">
                  <h2 className="font-fredoka text-2xl font-bold text-slate-800">
                    Kiddom Math Curriculum
                  </h2>
                  <p className="text-sm text-slate-600">
                    Sequenced Common Core scope &amp; sequence — Kindergarten through Grade 8
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mb-2 text-sm">
                <span className="text-slate-600 font-medium">Overall Progress</span>
                <span className="font-semibold text-red-600">{pct}%</span>
              </div>
              <Progress value={pct} className="h-3" />
            </CardContent>
          </Card>
        </motion.div>

        {/* Grade Tracks */}
        {groupedByGrade.length === 0 && (
          <Card className="border-0 shadow-md">
            <CardContent className="p-8 text-center">
              <p className="text-slate-600">No Kiddom lessons available yet.</p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-8">
          {groupedByGrade.map((group, gi) => {
            const gradeDone = group.lessons.filter(l => completedIds.has(l.id)).length;
            const gradePct = Math.round((gradeDone / group.lessons.length) * 100);
            const locked = isAboveBand(group.grade);
            return (
              <motion.section
                key={group.grade}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: gi * 0.05 }}
                data-testid={`kiddom-grade-section-${group.grade}`}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <h3 className="font-fredoka text-xl font-bold flex items-center gap-2" style={{ color: group.meta.color }}>
                      {group.meta.label}
                      {locked && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wide">
                          <Sparkles className="w-3 h-3" /> Coming up next!
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-slate-500">{group.meta.hint}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {gradeDone}/{group.lessons.length} • {gradePct}%
                  </Badge>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  {group.lessons.map((lesson) => {
                    const isDone = completedIds.has(lesson.id);
                    if (locked) {
                      return (
                        <div
                          key={lesson.id}
                          data-testid={`kiddom-locked-${lesson.id}`}
                          className="border-l-4 border-dashed border-slate-200 bg-slate-50/60 rounded-lg opacity-70 cursor-not-allowed"
                          title="Finish your current-grade lessons first!"
                        >
                          <div className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Lock className="w-5 h-5 text-slate-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-slate-500 truncate">{lesson.title}</h4>
                              <p className="text-xs text-slate-400 truncate">
                                Unlocks after your current grade
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={lesson.id}
                        to={`/activity/${lesson.id}?child=${childId}`}
                        data-testid={`kiddom-lesson-${lesson.id}`}
                      >
                        <Card className={`border-l-4 hover:shadow-md transition-all ${isDone ? 'bg-emerald-50' : 'bg-white'}`}
                              style={{ borderLeftColor: group.meta.color }}>
                          <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                              {isDone
                                ? <CheckCircle className="w-5 h-5 text-emerald-600" />
                                : <Play className="w-5 h-5 text-slate-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-slate-800 truncate">{lesson.title}</h4>
                              <p className="text-xs text-slate-500 truncate">
                                {lesson.content?.ccss || ''} {lesson.description ? `· ${lesson.description}` : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 text-amber-600 text-xs font-semibold">
                              <Star className="w-3 h-3 fill-amber-500" />
                              {lesson.xp_reward || 25}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </motion.section>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default KiddomMathPage;
